import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BRAND_COLORS,
  SUBJECTS,
  SUBJECT_THEME,
  getSkill,
  type MeResponse,
  type SubjectId,
} from '@lk/shared';
import { Icon, SUBJECT_ICON } from '../../components/Icon';
import { Panel } from '../../components/Panel';
import { ErrorState, LoadingState, EmptyState } from '../../components/States';
import { cn } from '../../lib/cn';
import { formatDate, formatPercent } from '../../lib/format';
import { progressQuery } from '../learner/queries';
import { useUiStore } from '../../store/uiStore';
import { useSelectedLearner } from './ParentLayout';

/**
 * Колір клітинки — від червоного (провисає) через золото до зеленого (опановано).
 * Свідомо не використовуємо лише відтінки одного кольору: батько має
 * знаходити проблему за секунду, скануючи мапу очима.
 */
function masteryColor(mastery: number): string {
  const clamped = Math.max(0, Math.min(1, mastery));
  if (clamped < 0.34) return BRAND_COLORS.red;
  if (clamped < 0.67) return BRAND_COLORS.gold;
  return BRAND_COLORS.green;
}

function masteryAlpha(mastery: number): number {
  return 0.28 + Math.max(0, Math.min(1, mastery)) * 0.62;
}

export function SkillsHeatmap({ me }: { me: MeResponse }): React.JSX.Element {
  const learner = useSelectedLearner(me);
  const days = useUiStore((s) => s.parentDays);
  const [subjectFilter, setSubjectFilter] = useState<SubjectId | 'all'>('all');

  const progress = useQuery({
    ...progressQuery(learner?.id ?? '', days),
    enabled: Boolean(learner?.id),
  });

  const grouped = useMemo(() => {
    const skills = progress.data?.skills ?? [];
    const map = new Map<SubjectId, Map<string, typeof skills>>();
    for (const skill of skills) {
      if (subjectFilter !== 'all' && skill.subject !== subjectFilter) continue;
      const strandKey = skill.strand || getSkill(skill.skillId)?.strand || 'інше';
      const bySubject = map.get(skill.subject) ?? new Map<string, typeof skills>();
      const list = bySubject.get(strandKey) ?? [];
      list.push(skill);
      bySubject.set(strandKey, list);
      map.set(skill.subject, bySubject);
    }
    return map;
  }, [progress.data, subjectFilter]);

  if (!learner) return <EmptyState title="Немає дитини для показу" />;
  if (progress.isLoading) return <LoadingState label="Будуємо мапу навичок…" />;
  if (progress.isError) {
    return <ErrorState error={progress.error} onRetry={() => void progress.refetch()} />;
  }

  const focus = new Set(progress.data?.focusSkills ?? []);
  const skills = progress.data?.skills ?? [];

  return (
    <div className="flex flex-col gap-4">
      <Panel className="p-4">
        <h2 className="text-lg font-black">Теплова мапа навичок · {learner.displayName}</h2>
        <p className="mt-1 text-xs font-semibold text-white/55">
          Кожна клітинка — окрема навичка. Червона зона означає, що тема провисає:
          саме туди система спрямовує більше питань і більший заробіток.
        </p>

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSubjectFilter('all')}
            aria-pressed={subjectFilter === 'all'}
            className={cn(
              'lk-chip cursor-pointer',
              subjectFilter === 'all' && 'border-[var(--lk-gold)] text-[var(--lk-gold)]',
            )}
          >
            усі предмети
          </button>
          {Object.values(SUBJECTS).map((subject) => (
            <button
              key={subject.id}
              type="button"
              onClick={() => setSubjectFilter(subject.id)}
              aria-pressed={subjectFilter === subject.id}
              className={cn(
                'lk-chip cursor-pointer',
                subjectFilter === subject.id && 'border-[var(--lk-gold)] text-[var(--lk-gold)]',
              )}
            >
              <Icon name={SUBJECT_ICON[subject.id]} size={14} />
              {subject.title}
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] font-bold text-white/50">
          <span>Легенда:</span>
          {[
            { label: 'провисає', color: BRAND_COLORS.red },
            { label: 'в роботі', color: BRAND_COLORS.gold },
            { label: 'опановано', color: BRAND_COLORS.green },
          ].map((entry) => (
            <span key={entry.label} className="flex items-center gap-1.5">
              <span
                className="h-3 w-6 rounded"
                style={{ background: entry.color, opacity: 0.75 }}
                aria-hidden
              />
              {entry.label}
            </span>
          ))}
          <span className="ml-auto">◆ — пріоритет системи</span>
        </div>
      </Panel>

      {skills.length === 0 ? (
        <EmptyState
          title="Мапа ще порожня"
          hint="Після перших сесій тут зʼявляться всі навички дитини."
        />
      ) : null}

      {[...grouped.entries()].map(([subjectId, strands]) => {
        const theme = SUBJECT_THEME[subjectId];
        return (
          <Panel key={subjectId} className="p-4" style={{ borderColor: `${theme.color}55` }}>
            <h3 className="flex items-center gap-2 text-base font-black">
              <Icon name={SUBJECT_ICON[subjectId]} size={20} />
              {SUBJECTS[subjectId].title}
            </h3>

            {[...strands.entries()].map(([strand, list]) => (
              <section key={strand} className="mt-3">
                <h4 className="text-[11px] font-black tracking-widest text-white/45 uppercase">
                  {strand}
                </h4>
                <ul className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                  {list.map((skill) => (
                    <li key={skill.skillId}>
                      <div
                        className="relative h-full rounded-[var(--lk-radius-sm)] border-2 p-2.5"
                        style={{
                          borderColor: focus.has(skill.skillId)
                            ? BRAND_COLORS.gold
                            : 'rgba(255,255,255,.14)',
                          background: `color-mix(in srgb, ${masteryColor(skill.mastery)} ${Math.round(
                            masteryAlpha(skill.mastery) * 100,
                          )}%, rgba(0,0,0,.55))`,
                        }}
                        title={`${skill.title}: опанування ${formatPercent(skill.mastery)}, ${skill.correct}/${skill.attempts} правильних`}
                      >
                        <p className="text-xs leading-tight font-black">
                          {focus.has(skill.skillId) ? '◆ ' : ''}
                          {skill.title}
                        </p>
                        <p className="tabular mt-1 text-[11px] font-bold text-white/80">
                          {formatPercent(skill.mastery)} · {skill.correct}/{skill.attempts}
                        </p>
                        <p className="mt-0.5 text-[10px] font-bold text-white/55">
                          Elo {Math.round(skill.elo)}
                          {skill.dueAt ? ` · повтор ${formatDate(skill.dueAt)}` : ''}
                        </p>
                        {skill.trend !== 0 ? (
                          <span
                            className="absolute top-2 right-2 text-[10px] font-black"
                            style={{
                              color: skill.trend > 0 ? BRAND_COLORS.green : BRAND_COLORS.red,
                            }}
                          >
                            {skill.trend > 0 ? '▲' : '▼'}
                          </span>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </Panel>
        );
      })}
    </div>
  );
}
